/**
 * Standard naming conventions for residential lighting sections
 */

export const STANDARD_SECTION_NAMES = [
  // Front Sections
  'Garage Eave',
  'Main Gable - Left Slope',
  'Main Gable - Right Slope',
  'Right Gable - Left Slope',
  'Right Gable - Right Slope',
  'Left Gable - Left Slope',
  'Left Gable - Right Slope',
  'Front Door Surround',
  'Front Eave - Left',
  'Front Eave - Right',
  'Front Porch Eave',
  'Front Porch Column - Left',
  'Front Porch Column - Right',
  'Front Window - Left',
  'Front Window - Right',
  
  // Side Sections
  'Left Side Eave',
  'Right Side Eave',
  'Left Side Garage',
  'Right Side Garage',
  
  // Rear Sections
  'Rear Eave - Left',
  'Rear Eave - Right',
  'Rear Gable - Left Slope',
  'Rear Gable - Right Slope',
  'Rear Door Surround',
  
  // Additional Features
  'Bay Window',
  'Dormer - Left',
  'Dormer - Right',
  'Chimney',
  'Column',
  'Railing',
  'Peak Accent',
  'Soffit',
  'Fascia',
];

/**
 * Get the next suggested section name based on existing sections
 */
export function getNextSectionName(existingSections, lineData = null) {
  const usedNames = new Set(existingSections.map(s => s.name));
  
  if (lineData) {
    const suggested = suggestNameFromLineData(lineData, existingSections);
    if (suggested && !usedNames.has(suggested)) {
      return suggested;
    }
  }
  
  // Find first unused standard name
  for (const name of STANDARD_SECTION_NAMES) {
    if (!usedNames.has(name)) {
      return name;
    }
  }
  
  // If all standard names used, fall back to numbered
  return `Section ${existingSections.length + 1}`;
}

function suggestNameFromLineData(lineData, existingSections) {
  const { start_x, start_y, end_x, end_y, length_feet } = lineData;
  const dx = end_x - start_x;
  const dy = end_y - start_y;
  const angle = Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);
  const isHorizontal = angle < 20 || angle > 160;
  const isVertical = angle > 70 && angle < 110;
  const isDiagonal = !isHorizontal && !isVertical;
  const midX = (start_x + end_x) / 2;
  const midY = (start_y + end_y) / 2;
  const canvasWidth = 1200;
  const canvasHeight = 900;
  const isLeft = midX < canvasWidth * 0.4;
  const isRight = midX > canvasWidth * 0.6;
  const isTop = midY < canvasHeight * 0.4;
  
  if (length_feet < 15) {
    if (isVertical) {
      const hasLeftColumn = existingSections.some(s => s.name.includes('Column - Left'));
      const hasRightColumn = existingSections.some(s => s.name.includes('Column - Right'));
      if (isLeft && !hasLeftColumn) return 'Front Porch Column - Left';
      if (isRight && !hasRightColumn) return 'Front Porch Column - Right';
      return 'Column';
    }
    if (isHorizontal) {
      const hasDoorSurround = existingSections.some(s => s.name.includes('Door Surround'));
      if (!hasDoorSurround) return 'Front Door Surround';
      const hasLeftWindow = existingSections.some(s => s.name.includes('Window - Left'));
      const hasRightWindow = existingSections.some(s => s.name.includes('Window - Right'));
      if (isLeft && !hasLeftWindow) return 'Front Window - Left';
      if (isRight && !hasRightWindow) return 'Front Window - Right';
      return 'Window Feature';
    }
  }
  
  if (length_feet >= 15 && length_feet < 25) {
    if (isHorizontal) {
      if (isLeft) return 'Front Eave - Left';
      if (isRight) return 'Front Eave - Right';
      return 'Side Eave';
    }
    if (isDiagonal) {
      const hasMainGableLeft = existingSections.some(s => s.name === 'Main Gable - Left Slope');
      const hasMainGableRight = existingSections.some(s => s.name === 'Main Gable - Right Slope');
      if (isLeft && !hasMainGableLeft) return 'Main Gable - Left Slope';
      if (isRight && !hasMainGableRight) return 'Main Gable - Right Slope';
      return 'Gable - Left Slope';
    }
  }
  
  if (length_feet >= 25) {
    if (isHorizontal) {
      const hasGarageEave = existingSections.some(s => s.name === 'Garage Eave');
      if (!hasGarageEave && !isTop) return 'Garage Eave';
      if (isLeft) return 'Left Side Eave';
      if (isRight) return 'Right Side Eave';
      return 'Front Eave';
    }
    if (isDiagonal && isTop) {
      const hasMainGableLeft = existingSections.some(s => s.name === 'Main Gable - Left Slope');
      const hasMainGableRight = existingSections.some(s => s.name === 'Main Gable - Right Slope');
      if (isLeft && !hasMainGableLeft) return 'Main Gable - Left Slope';
      if (isRight && !hasMainGableRight) return 'Main Gable - Right Slope';
    }
  }
  
  return null;
}

/**
 * Common section name categories for organization
 */
export const SECTION_CATEGORIES = {
  'Front': ['Front Garage Eave', 'Main Gable - Left Slope', 'Main Gable - Right Slope', 'Main Gable - Peak', 'Front Door Surround', 'Front Eave - Left', 'Front Eave - Right', 'Front Porch Eave', 'Front Porch Column - Left', 'Front Porch Column - Right', 'Front Window - Left', 'Front Window - Right'],
  'Side': ['Left Side Eave', 'Right Side Eave', 'Left Side Garage', 'Right Side Garage'],
  'Rear': ['Rear Eave - Left', 'Rear Eave - Right', 'Rear Gable - Left Slope', 'Rear Gable - Right Slope', 'Rear Door Surround'],
  'Features': ['Bay Window', 'Dormer - Left', 'Dormer - Right', 'Chimney', 'Column', 'Railing', 'Peak Accent', 'Soffit', 'Fascia'],
};
